import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { Button } from '@anytag/shared-atoms';
import { THEME } from '@anytag/shared-constants';
import { AppLanguage } from '@anytag/shared-types';
import { ActionButtons } from '@anytag/shared-molecules';
import { cn, localizedMonthsShort } from '@anytag/shared-utils';

export interface MonthPickerDropdownProps {
  value?: Date;
  className?: string;
  onCancel?: () => void;
  onSubmit?: () => void;
  disabledMonths?: { before?: Date; after?: Date };
  onChange?: (data: { month: number; year: number }) => void;
}

export const MonthPickerDropdown = ({
  value,
  onCancel,
  onSubmit,
  onChange,
  className,
  disabledMonths
}: MonthPickerDropdownProps) => {
  const month = value?.getMonth();
  const year = value?.getFullYear() || new Date().getFullYear();

  const { i18n } = useTranslation();
  const [displayedYear, setDisplayedYear] = useState(year);

  const months = localizedMonthsShort(i18n.language as AppLanguage);

  const checkDisabledMonth = (monthIndex: number) => {
    if (disabledMonths?.before) {
      const disabledBeforeMonth = disabledMonths.before.getMonth();
      const disabledBeforeYear = disabledMonths.before.getFullYear();

      return (
        displayedYear < disabledBeforeYear || (disabledBeforeYear === displayedYear && monthIndex < disabledBeforeMonth)
      );
    }

    if (disabledMonths?.after) {
      const disabledAfterMonth = disabledMonths.after.getMonth();
      const disabledAfterYear = disabledMonths.after.getFullYear();

      return (
        displayedYear > disabledAfterYear || (disabledAfterYear === displayedYear && monthIndex > disabledAfterMonth)
      );
    }
  };

  const checkDisabledYear = (year: number) => {
    if (disabledMonths?.before) {
      const disabledBeforeYear = disabledMonths.before.getFullYear();

      return year < disabledBeforeYear;
    }

    if (disabledMonths?.after) {
      const disabledAfterYear = disabledMonths.after.getFullYear();

      return year > disabledAfterYear;
    }
  };

  return (
    <div className={cn(className)}>
      <div className="box-border p-6">
        <div className="flex items-center justify-between gap-2.5">
          <Button
            variant="outline"
            css={styles.arrow}
            disabled={checkDisabledYear(displayedYear - 1)}
            onClick={() => setDisplayedYear(displayedYear - 1)}
            prefixIcon={{ size: 12, icon: 'caret-left', variant: 'gray' }}
          />
          <p css={{ fontWeight: 600, fontSize: '16px', lineHeight: '24px', textAlign: 'center' }}>{displayedYear}</p>
          <Button
            variant="outline"
            css={styles.arrow}
            disabled={checkDisabledYear(displayedYear + 1)}
            onClick={() => setDisplayedYear(displayedYear + 1)}
            prefixIcon={{ size: 12, icon: 'caret-right', variant: 'gray' }}
          />
        </div>

        <div className="m-[4px_-4px_-4px] flex w-70 flex-wrap">
          {months.map((name, index) => {
            const disabled = checkDisabledMonth(index);

            return (
              <div key={`month-${index}`} className="box-border flex-1/3 p-1">
                <button
                  css={styles.month}
                  disabled={disabled}
                  onClick={() => onChange?.({ month: index, year: displayedYear })}
                  className={index === month && displayedYear === year ? 'active' : undefined}
                >
                  {name}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <ActionButtons
        buttonHeight="32px"
        primaryButtonProps={{ onClick: onSubmit }}
        secondaryButtonProps={{ onClick: onCancel }}
        hasTopBorder
      />
    </div>
  );
};
const styles = {
  arrow: css({
    padding: 0,
    border: 'none',
    '&:not(:disabled):hover > svg': { filter: 'brightness(0.7)' },
    '&:disabled': { background: 'transparent', opacity: 0.25, cursor: 'default' }
  }),
  month: css({
    width: '100%',
    height: '35px',
    display: 'flex',
    borderRadius: '3px',
    alignItems: 'center',
    boxSizing: 'border-box',
    justifyContent: 'center',
    transition: 'background-color 0.1s ease, color 0.1s ease',
    '&:disabled': {
      opacity: 0.25,
      cursor: 'default',
      pointerEvents: 'none'
    },
    '&:not(:disabled)': {
      '&:hover': {
        cursor: 'pointer',
        color: THEME.datePicker.colors.active.text,
        backgroundColor: THEME.background.colors.blue
      },
      '&.active': {
        color: THEME.datePicker.colors.active.text,
        backgroundColor: THEME.datePicker.colors.active.background
      }
    }
  })
};
